import { IcInfoWarning } from '@assets/icons/24x24';
import { Box, HStack, Icon, Separator, Text } from '@chakra-ui/react';
import { useFlightPlansManagementContext } from '@components/cockpit/panels/flightPlan/FlightPlansManagementContext';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import useListFormatter from '@hooks/useListFormatter';
import { selectActiveGroup } from '@redux/authent/authent.selectors';
import { selectOrganizationByCode } from '@redux/config/config.selectors';
import { formatDate } from '@utils/date.utils';
import { formatUnit, HeightUnits } from '@utils/unit.utils';
import { format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';

import { DateTimeEnum } from '@/types/dateTime.types';
import { ActivationAndFlightPlan } from '@/types/utm/flightPlan.types';

import ActivationButtons from './ActivationButtons';
import FlightPlanStatusIcon from './FlightPlanStatusIcon';

type Props = {
  activationAndFlightPlan: ActivationAndFlightPlan;
  index: number;
};

export default function ActivationAndFlightPlanRow({ activationAndFlightPlan, index }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const formatList = useListFormatter();
  const {
    activationAndFlightPlans,
    selectedActivationAndFlightPlanActivationId,
    setSelectedActivationAndFlightPlanActivationId,
  } = useFlightPlansManagementContext();
  const organizationCode = useAppSelector(selectActiveGroup)?.organization;
  const organization = useAppSelector((state) => selectOrganizationByCode(state, organizationCode ?? ''));
  const isSelected = selectedActivationAndFlightPlanActivationId === activationAndFlightPlan.activation.id;

  const formattedStartDate = formatDate(activationAndFlightPlan.activation.startDate, DateTimeEnum.DATE);
  const formattedEndDate = formatDate(activationAndFlightPlan.activation.endDate, DateTimeEnum.DATE);
  const formattedStartTime = format(activationAndFlightPlan.activation.startDate, "HH':'mm'");
  const formattedEndTime = format(activationAndFlightPlan.activation.endDate, "HH':'mm'");

  const isSameDay = formattedStartDate === formattedEndDate;

  const providerName = (): string => {
    if (activationAndFlightPlan.flightPlan.provider === organizationCode) {
      return formatMessage({ id: 'global.me' });
    } else if (activationAndFlightPlan.flightPlan.externalId !== null) {
      return activationAndFlightPlan.flightPlan.provider;
    }
    return organization?.name ?? formatMessage({ id: 'components.flightPlans.unknownSource' });
  };

  return (
    <>
      {index !== 0 && <Separator size="md" borderColor="neutral.900" />}
      <HStack
        width="100%"
        gap={2}
        paddingLeft={5}
        height="56px"
        flexShrink={0}
        cursor="pointer"
        backgroundColor={isSelected ? 'neutral.850' : 'transparent'}
        _hover={{ backgroundColor: isSelected ? 'neutral.850' : 'neutral.700' }}
        onClick={() =>
          setSelectedActivationAndFlightPlanActivationId(isSelected ? null : activationAndFlightPlan.activation.id)
        }
      >
        <Tooltip content={activationAndFlightPlan.flightPlan.name}>
          <Text width="200px" fontWeight="medium" lineClamp={1} wordBreak="break-all">
            {activationAndFlightPlan.flightPlan.name}
          </Text>
        </Tooltip>
        <Text color="neutral.300" width="368px">
          <FormattedMessage
            id={isSameDay ? 'components.flightPlans.sameDay' : 'components.flightPlans.multipleDays'}
            values={{
              startDate: (
                <Text as="span" key="formattedStartDate" color="neutral.white">
                  {formattedStartDate}
                </Text>
              ),
              startTime: (
                <Text as="span" key="formattedStartTime" color="neutral.white">
                  {formattedStartTime}
                </Text>
              ),
              endDate: (
                <Text as="span" key="formattedEndDate" color="neutral.white">
                  {formattedEndDate}
                </Text>
              ),
              endTime: (
                <Text as="span" key="formattedEndTime" color="neutral.white">
                  {formattedEndTime}
                </Text>
              ),
            }}
          />
        </Text>
        <Text width="96px">{formatUnit(HeightUnits.METERS, activationAndFlightPlan.flightPlan.heightMax)}</Text>
        <Text width="160px">{providerName()}</Text>
        <Box width="64px" paddingLeft={2}>
          <FlightPlanStatusIcon
            status={activationAndFlightPlan.activation.status}
            validatedOrRejectedBy={activationAndFlightPlan.activation.validatedOrRejectedBy}
            validationOrRejectionTime={activationAndFlightPlan.activation.validationOrRejectionTime}
          />
        </Box>
        <Box width="68px" paddingLeft={2}>
          {activationAndFlightPlan.activation.conflicts.length > 0 ? (
            <Tooltip
              content={formatList(
                activationAndFlightPlan.activation.conflicts.map((conflit) => conflit.conflictingFlightPlanCode),
              )}
            >
              <Icon as={IcInfoWarning} color="red.500" boxSize="24px" />
            </Tooltip>
          ) : (
            <Box backgroundColor="neutral.500" width="8px" height="3px" marginLeft={1} />
          )}
        </Box>
        <Box marginLeft="auto">
          <ActivationButtons activationAndFlightPlan={activationAndFlightPlan} />
        </Box>
      </HStack>
      {index === activationAndFlightPlans.length - 1 && <Separator size="md" borderColor="neutral.900" />}
    </>
  );
}
